import React, { useEffect, useState, useCallback } from 'react';
import {
  FileText, Plus, Pencil, Trash2, Eye, EyeOff, Archive,
  ArrowUp, ArrowDown, FolderPlus, Loader2, Tag,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { TiptapEditor } from './documentTemplates/TiptapEditor';
import { helpAdminService } from '../../services/helpAdmin.service';

const STATUS_COLORS = {
  published: 'bg-green-100 text-green-800',
  draft: 'bg-amber-100 text-amber-800',
  archived: 'bg-gray-200 text-gray-600',
};

const emptyArticle = {
  title: '', category_id: '', summary: '', keywords: '', status: 'draft', body_html: '', order: 999,
};

const AdminHelpCenter = () => {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');

  // article dialog
  const [articleOpen, setArticleOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);

  // category dialog
  const [catOpen, setCatOpen] = useState(false);
  const [catEditingId, setCatEditingId] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', icon: '', order: 999, status: 'active' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, arts] = await Promise.all([
        helpAdminService.getCategories(),
        helpAdminService.getArticles(filterCat === 'all' ? undefined : filterCat),
      ]);
      setCategories(cats);
      setArticles(arts);
    } catch (e) {
      toast.error('Failed to load Help content.');
    } finally {
      setLoading(false);
    }
  }, [filterCat]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || '—';

  // ── Article actions ──
  const openNewArticle = () => {
    setEditingId(null);
    setForm({ ...emptyArticle, category_id: categories[0]?.id || '' });
    setArticleOpen(true);
  };

  const openEditArticle = async (id) => {
    try {
      const a = await helpAdminService.getArticle(id);
      setEditingId(id);
      setForm({
        title: a.title, category_id: a.category_id || '', summary: a.summary || '',
        keywords: (a.keywords || []).join(', '), status: a.status, body_html: a.body_html || '',
        order: a.order ?? 999,
      });
      setArticleOpen(true);
    } catch (e) {
      toast.error('Could not load article.');
    }
  };

  const saveArticle = async () => {
    if (!form.title.trim()) { toast.error('Title is required.'); return; }
    if (!form.category_id) { toast.error('Please choose a category.'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        category_id: form.category_id,
        summary: form.summary,
        keywords: form.keywords.split(',').map((k) => k.trim()).filter(Boolean),
        status: form.status,
        body_html: form.body_html,
        order: Number(form.order) || 999,
      };
      if (editingId) {
        await helpAdminService.updateArticle(editingId, payload);
        toast.success('Article updated.');
      } else {
        await helpAdminService.createArticle(payload);
        toast.success('Article created.');
      }
      setArticleOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save article.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (a, status) => {
    try {
      await helpAdminService.setArticleStatus(a.id, status);
      toast.success(`Article ${status}.`);
      loadAll();
    } catch (e) {
      toast.error('Failed to update status.');
    }
  };

  const deleteArticle = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await helpAdminService.deleteArticle(a.id);
      toast.success('Article deleted.');
      loadAll();
    } catch (e) {
      toast.error('Failed to delete article.');
    }
  };

  const move = async (index, dir) => {
    const list = [...articles];
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    const a = list[index];
    const b = list[target];
    try {
      await helpAdminService.reorderArticles([
        { id: a.id, order: b.order ?? target },
        { id: b.id, order: a.order ?? index },
      ]);
      loadAll();
    } catch (e) {
      toast.error('Failed to reorder.');
    }
  };

  // ── Category actions ──
  const openNewCategory = () => {
    setCatEditingId(null);
    setCatForm({ name: '', icon: '', order: 999, status: 'active' });
    setCatOpen(true);
  };
  const openEditCategory = (c) => {
    setCatEditingId(c.id);
    setCatForm({ name: c.name, icon: c.icon || '', order: c.order ?? 999, status: c.status });
    setCatOpen(true);
  };
  const saveCategory = async () => {
    if (!catForm.name.trim()) { toast.error('Category name is required.'); return; }
    try {
      const payload = { name: catForm.name.trim(), icon: catForm.icon, order: Number(catForm.order) || 999, status: catForm.status };
      if (catEditingId) {
        await helpAdminService.updateCategory(catEditingId, payload);
        toast.success('Category updated.');
      } else {
        await helpAdminService.createCategory(payload);
        toast.success('Category created.');
      }
      setCatOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save category.');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-help-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-amber-500" /> Help Center
          </h1>
          <p className="text-slate-500 text-sm">Author the searchable client knowledge base. Only published articles are visible to clients.</p>
        </div>
      </div>

      <Tabs defaultValue="articles">
        <TabsList>
          <TabsTrigger value="articles" data-testid="admin-help-tab-articles">Articles</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-help-tab-categories">Categories</TabsTrigger>
        </TabsList>

        {/* Articles */}
        <TabsContent value="articles" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="w-64">
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger data-testid="admin-help-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={openNewArticle} className="bg-amber-500 hover:bg-amber-600" data-testid="admin-help-new-article-btn">
              <Plus className="h-4 w-4 mr-2" /> New article
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <Card>
              <CardContent className="p-0 divide-y">
                {articles.length === 0 && <div className="p-8 text-center text-gray-500">No articles yet.</div>}
                {articles.map((a, i) => (
                  <div key={a.id} className="flex items-center gap-3 p-4" data-testid={`admin-help-article-row-${a.slug}`}>
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
                      <button onClick={() => move(i, 1)} disabled={i === articles.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 truncate">{a.title}</p>
                        <Badge className={`text-xs ${STATUS_COLORS[a.status]}`}>{a.status}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{catName(a.category_id)} · 👍 {a.helpful_count} · 👁 {a.view_count}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {a.status !== 'published' ? (
                        <Button variant="ghost" size="icon" onClick={() => toggleStatus(a, 'published')} title="Publish" data-testid={`admin-help-publish-${a.slug}`}><Eye className="h-4 w-4 text-green-600" /></Button>
                      ) : (
                        <Button variant="ghost" size="icon" onClick={() => toggleStatus(a, 'draft')} title="Unpublish" data-testid={`admin-help-unpublish-${a.slug}`}><EyeOff className="h-4 w-4 text-amber-600" /></Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => toggleStatus(a, 'archived')} title="Archive" data-testid={`admin-help-archive-${a.slug}`}><Archive className="h-4 w-4 text-gray-500" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditArticle(a.id)} title="Edit" data-testid={`admin-help-edit-${a.slug}`}><Pencil className="h-4 w-4 text-blue-600" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteArticle(a)} title="Delete" data-testid={`admin-help-delete-${a.slug}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Categories */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewCategory} className="bg-amber-500 hover:bg-amber-600" data-testid="admin-help-new-category-btn">
              <FolderPlus className="h-4 w-4 mr-2" /> New category
            </Button>
          </div>
          <Card>
            <CardContent className="p-0 divide-y">
              {categories.length === 0 && <div className="p-8 text-center text-gray-500">No categories yet.</div>}
              {categories.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-4" data-testid={`admin-help-cat-row-${c.slug}`}>
                  <div>
                    <p className="font-medium text-slate-900 flex items-center gap-2"><Tag className="h-4 w-4 text-amber-500" />{c.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">/{c.slug} · order {c.order} · {c.article_count} articles · {c.status}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openEditCategory(c)} title="Edit"><Pencil className="h-4 w-4 text-blue-600" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Article dialog */}
      <Dialog open={articleOpen} onOpenChange={setArticleOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-help-article-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Edit article' : 'New article'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-help-article-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="admin-help-article-category"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="admin-help-article-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Summary</Label>
              <Textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} data-testid="admin-help-article-summary" />
            </div>
            <div>
              <Label>Keywords (comma-separated)</Label>
              <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="invoice, payment, card" data-testid="admin-help-article-keywords" />
            </div>
            <div>
              <Label>Body</Label>
              <TiptapEditor content={form.body_html} onChange={(html) => setForm({ ...form, body_html: html })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleOpen(false)}>Cancel</Button>
            <Button onClick={saveArticle} disabled={saving} className="bg-amber-500 hover:bg-amber-600" data-testid="admin-help-save-article-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save article'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent data-testid="admin-help-category-dialog">
          <DialogHeader><DialogTitle>{catEditingId ? 'Edit category' : 'New category'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="admin-help-category-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon (optional)</Label>
                <Input value={catForm.icon} onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })} placeholder="rocket" />
              </div>
              <div>
                <Label>Order</Label>
                <Input type="number" value={catForm.order} onChange={(e) => setCatForm({ ...catForm, order: e.target.value })} />
              </div>
            </div>
            {catEditingId && (
              <div>
                <Label>Status</Label>
                <Select value={catForm.status} onValueChange={(v) => setCatForm({ ...catForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Cancel</Button>
            <Button onClick={saveCategory} className="bg-amber-500 hover:bg-amber-600" data-testid="admin-help-save-category-btn">Save category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHelpCenter;
